import { Component, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService, AuthService, I18nService, ThemeService, ToastService, Response, ErrorResponse } from '@toteimlib/angular-lib';
import { OrganizationService } from './services/organization.service';
import { fromEvent, map, merge, of, Subscription } from 'rxjs';
import { FirebaseService } from './services/firebase.service';
import { ToastModule } from '@openng/optimus-ui/toast';
import { Card } from '@openng/optimus-ui/card';
import { environment } from 'environments/environment';
import Intercom from '@intercom/messenger-js-sdk';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    ToastModule,
    Card
  ],
  providers: [
    ToastService
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {

  theme: Signal<string> = signal('light');

  networkStatusSubscription: Subscription = Subscription.EMPTY;
  routerSubscription: Subscription = Subscription.EMPTY;

  showMaintenance: WritableSignal<boolean> = signal(false);
  maintenanceMessage: WritableSignal<string | null> = signal(null);
  networkStatus: WritableSignal<boolean> = signal(false);

  constructor(private translateService: TranslateService, private organizationService: OrganizationService, private toastService: ToastService, private router: Router, private firebaseService: FirebaseService, private i18nService: I18nService, private apiService: ApiService, private authService: AuthService, private themeService: ThemeService) {
    const userLang: string = this.i18nService.getLang()();

    this.theme = this.themeService.getTheme();

    this.translateService.addLangs(environment.langs);
    this.translateService.setDefaultLang('fr');

    this.apiService.initialize(environment.api_url, environment.version, 'organization_id', '/dashboard/home', '/login');

    this.apiService.addBeforeMiddleware((res: Response | ErrorResponse): boolean => {
      if (res.error && res.error === 404 && res.message === 'error.organization-not-found') {
        this.organizationService.removeOrganization();
        this.toastService.showError(res.message);

        return false;
      }

      return true;
    });

    this.authService.setLocalStorageList([
      'api_token',
      'user',
      'organization_id',
      'organization'
    ]);

    if (environment.langs.includes(userLang)) {
      this.translateService.use(userLang);
    }
    else {
      this.translateService.use('fr');
    }

    if (environment.production) {
      Intercom({
        app_id: environment.intercom_app_id,
        Origin: 'ToteimManager'
      });
    }

    this.routerSubscription = this.router.events.subscribe((val: any): void => {
      if (val instanceof NavigationStart) {
        this.getRemoteConfig();
      }
    });
  }

  ngOnInit(): void {
    this.checkNetworkStatus();
    this.getRemoteConfig();
  }

  ngOnDestroy(): void {
    this.networkStatusSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  getRemoteConfig(): void {
    this.firebaseService.isMaintenanceMode().then((res: boolean): void => {
      this.showMaintenance.set(res);
    });

    this.firebaseService.getMaintenanceMessage(this.i18nService.getLang()()).then((res: any): void => {
      this.maintenanceMessage.set(res);
    });
  }

  checkNetworkStatus(): void {
    this.networkStatus.set(navigator.onLine);

    this.networkStatusSubscription = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map((): boolean => navigator.onLine))
      .subscribe((status: boolean): void => {
        this.networkStatus.set(status);
      });
  }

}
